import { ChangeDetectionStrategy, Component, inject, input, output, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { ZagDatePipe, ZagIcon } from '@zag/ui';
import type { ApiKeyListRowData } from './api-key-list-row.component';

/**
 * Card view for a single API key.
 * Matches the pattern of `RoleCard` in `libs/common/ui`.
 */
@Component({
  selector: 'sc-api-key-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [TranslatePipe, ZagIcon, ZagDatePipe],
  host: {
    '[class.sc-api-key-card]': 'true',
    '[attr.role]': '"button"',
    '[attr.tabindex]': '0',
    '(click)': 'onCardClick()',
    '(keydown)': 'onCardKeydown($event)',
  },
  template: `
    <div class="sc-api-key-card__header">
      <span zagIcon="vpn_key" class="sc-api-key-card__icon"></span>
      <div class="sc-api-key-card__actions">
        <button
          type="button"
          class="sc-api-key-card__action"
          [disabled]="disableActions()"
          [attr.aria-label]="apiKey().enabled ? ('apiKeys.action.disable' | translate) : ('apiKeys.action.enable' | translate)"
          (click)="onToggleClick($event)"
        >
          <span [zagIcon]="apiKey().enabled ? 'toggle_on' : 'toggle_off'"></span>
        </button>
        <button
          type="button"
          class="sc-api-key-card__action sc-api-key-card__action--delete"
          [disabled]="deleteDisabled()"
          [attr.aria-label]="'common.delete' | translate"
          (click)="onDeleteClick($event)"
        >
          <span zagIcon="delete"></span>
        </button>
      </div>
    </div>

    <div class="sc-api-key-card__body">
      <span class="sc-api-key-card__name">{{ apiKey().name || ('apiKeys.unnamed' | translate) }}</span>
      @if (apiKey().start) {
        <span class="sc-api-key-card__prefix">{{ apiKey().start }}…</span>
      }
    </div>

    <div class="sc-api-key-card__footer">
      <span
        class="sc-api-key-card__badge"
        [class.sc-api-key-card__badge--success]="apiKey().inheritsAllPermissions"
        [class.sc-api-key-card__badge--warning]="!apiKey().inheritsAllPermissions"
      >
        <span zagIcon="key"></span>
        @if (apiKey().inheritsAllPermissions) {
          {{ 'apiKeys.scope.inheritAll' | translate }}
        } @else {
          {{ 'apiKeys.scope.restricted' | translate: { count: apiKey().restrictedPermissionCount } }}
        }
      </span>
      <span
        class="sc-api-key-card__badge"
        [class.sc-api-key-card__badge--success]="apiKey().enabled"
        [class.sc-api-key-card__badge--error]="!apiKey().enabled"
      >
        {{ apiKey().enabled ? ('apiKeys.status.active' | translate) : ('apiKeys.status.disabled' | translate) }}
      </span>
      <span class="sc-api-key-card__date">{{ apiKey().createdAt | zagDate }}</span>
    </div>
  `,
  styles: [
    `
      .sc-api-key-card {
        display: flex;
        flex-direction: column;
        padding: var(--spacing-md);
        gap: var(--spacing-sm);
        background-color: var(--surface-color-default);
        border: 1px solid var(--border-color-default);
        border-radius: var(--border-radius-md);
        cursor: pointer;
        transition:
          box-shadow 0.15s ease,
          border-color 0.15s ease;
      }

      .sc-api-key-card:hover {
        border-color: var(--color-primary-light);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
      }

      .sc-api-key-card:focus-visible {
        outline: 2px solid var(--color-primary);
        outline-offset: 2px;
      }

      .sc-api-key-card__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      .sc-api-key-card__icon {
        font-size: 24px;
        color: var(--color-primary);
      }

      .sc-api-key-card__actions {
        display: flex;
        gap: var(--spacing-xs);
      }

      .sc-api-key-card__action {
        padding: var(--spacing-xs);
        border: none;
        background: none;
        border-radius: var(--border-radius-sm);
        color: var(--text-color-secondary);
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition:
          background-color 0.15s,
          color 0.15s;
      }

      .sc-api-key-card__action:hover {
        background-color: var(--color-info-light);
        color: var(--color-primary);
      }

      .sc-api-key-card__action:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .sc-api-key-card__action [zagIcon] {
        font-size: 18px;
      }

      .sc-api-key-card__action--delete:hover:not(:disabled) {
        background-color: var(--color-error-light);
        color: var(--color-error);
      }

      .sc-api-key-card__body {
        display: flex;
        flex-direction: column;
        gap: 2px;
      }

      .sc-api-key-card__name {
        font-weight: 500;
        color: var(--text-color-primary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .sc-api-key-card__prefix {
        font-family: monospace;
        font-size: var(--font-size-sm);
        color: var(--text-color-secondary);
      }

      .sc-api-key-card__footer {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
        flex-wrap: wrap;
        margin-top: auto;
      }

      .sc-api-key-card__badge {
        display: inline-flex;
        align-items: center;
        gap: var(--spacing-xs);
        font-size: var(--font-size-xs);
        padding: 2px var(--spacing-xs);
        border-radius: var(--border-radius-sm);
        border: 1px solid var(--border-color-default);
      }

      .sc-api-key-card__badge [zagIcon] {
        font-size: 12px;
      }

      .sc-api-key-card__badge--success {
        background-color: var(--color-success-light);
        border-color: var(--color-success);
        color: var(--color-success);
      }

      .sc-api-key-card__badge--warning {
        background-color: var(--color-warning-light);
        border-color: var(--color-warning);
        color: var(--color-warning);
      }

      .sc-api-key-card__badge--error {
        background-color: var(--color-error-light);
        border-color: var(--color-error);
        color: var(--color-error);
      }

      .sc-api-key-card__date {
        font-size: var(--font-size-xs);
        color: var(--text-color-tertiary);
        margin-left: auto;
      }
    `,
  ],
})
export class ApiKeyCardComponent {
  private readonly router = inject(Router);

  readonly apiKey = input.required<ApiKeyListRowData>();
  readonly editRoute = input<string[]>();
  readonly disableActions = input(false);
  readonly deleteDisabled = input(false);

  readonly toggleEnabled = output<ApiKeyListRowData>();
  readonly deleteKey = output<ApiKeyListRowData>();

  onCardClick(): void {
    const route = this.editRoute();
    if (route) void this.router.navigate(route);
  }

  onCardKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('button')) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onCardClick();
    }
  }

  onToggleClick(event: Event): void {
    event.stopPropagation();
    this.toggleEnabled.emit(this.apiKey());
  }

  onDeleteClick(event: Event): void {
    event.stopPropagation();
    this.deleteKey.emit(this.apiKey());
  }
}
